import { useEffect, useRef, useState } from "react";
import { Sparkles, BookOpen, LogIn, LogOut, User as UserIcon } from "lucide-react";

import { useAuth } from "@/lib/auth";

export const HeroHeader = ({ savedCount = 0 }) => {
  const { user, isAuthed, loading, startLogin, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  return (
    <header
      className="relative border-b border-white/5"
      data-testid="hikari-header"
    >
      <div className="hk-grid absolute inset-0 opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: "#060F1A",
              border: "2px solid #00BFA5",
              boxShadow: "0 0 24px -8px rgba(0,191,165,0.6)",
            }}
          >
            <Sparkles size={18} color="#00BFA5" strokeWidth={2.2} />
          </div>
          <div className="leading-tight">
            <div className="flex items-baseline gap-2">
              <span
                className="font-display"
                style={{
                  color: "#00BFA5",
                  fontWeight: 900,
                  fontSize: 22,
                  letterSpacing: "0.02em",
                }}
              >
                HIKARI
              </span>
              <span
                className="font-display"
                style={{ color: "#E8F0F7", fontWeight: 700, fontSize: 22 }}
              >
                STUDY
              </span>
            </div>
            <div
              className="font-mono"
              style={{ fontSize: 11, color: "#7A9BB5", letterSpacing: "0.12em" }}
            >
              SOCRATIC · STUDY · COMPANION
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div
            className="hk-chip hk-chip-accent hidden sm:inline-flex"
            data-testid="saved-count-chip"
          >
            <BookOpen size={12} />
            <span data-testid="saved-count-value">{savedCount}</span>
            <span>saved sets</span>
          </div>

          {loading ? (
            <div
              className="hk-chip"
              style={{ minWidth: 120, justifyContent: "center" }}
              data-testid="auth-loading"
            >
              <span className="hk-pulse">checking…</span>
            </div>
          ) : isAuthed ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 transition"
                style={{
                  background: "rgba(13,35,64,0.6)",
                  border: "1px solid rgba(0,191,165,0.35)",
                }}
                data-testid="profile-button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-7 h-7 rounded-full object-cover"
                    style={{ border: "1px solid rgba(232,240,247,0.18)" }}
                  />
                ) : (
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{
                      background: "#00BFA5",
                      color: "#032722",
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span
                  className="text-sm hidden sm:inline"
                  style={{ color: "#E8F0F7", maxWidth: 120 }}
                  data-testid="profile-name"
                >
                  {user.name || user.email}
                </span>
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 hk-card-strong p-3 z-40"
                  data-testid="profile-menu"
                  role="menu"
                >
                  <div className="px-2 py-1.5 mb-1">
                    <div
                      style={{ color: "#E8F0F7", fontSize: 14, fontWeight: 600 }}
                    >
                      {user.name || "Student"}
                    </div>
                    <div
                      className="truncate"
                      style={{ color: "#7A9BB5", fontSize: 12 }}
                      data-testid="profile-email"
                    >
                      {user.email}
                    </div>
                  </div>
                  <div className="hk-divider mb-1" />
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      signOut();
                    }}
                    className="w-full text-left rounded-md px-2 py-1.5 text-sm flex items-center gap-2 transition"
                    style={{ color: "#E8F0F7" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(232,64,64,0.08)";
                      e.currentTarget.style.color = "#E84040";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "#E8F0F7";
                    }}
                    data-testid="sign-out-btn"
                  >
                    <LogOut size={13} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={startLogin}
              className="hk-btn-accent px-4 py-2 text-sm flex items-center gap-1.5"
              data-testid="sign-in-btn"
            >
              <LogIn size={14} /> Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default HeroHeader;
